import React, { useEffect, useState, ChangeEvent, useRef } from "react";
import moment from "moment";
import {
  Button,
  DialogTitle,
  TextField,
  InputLabel,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormHelperText,
  MenuItem,
} from "@material-ui/core";
import ListSubheader from "@material-ui/core/ListSubheader";
import { useStyles } from "./styles";
import Draggable from "react-draggable";
import { Dialog, Paper, PaperProps } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useSelector } from "react-redux";
import { updateDiscussedPatient } from "../BoardRoundContainer/Function";
import { RootState } from "../../redux/rootReducer";
import { useAppDispatch } from "../../redux/index";
import {
  setReduxTempPatients,
  setReduxFilteredPatients,
} from "../../redux/patient";
import {
  savedInvestigationGroups,
  formatSavedInvestigation,
  getSavedInvestigationOptionValue,
  getSavedInvestigationOptionLabel,
} from "../../constants/savedInvestigations";

const defaultAllocateDr = {
  allocateDoctor: "N",
  allocatedDatetime: "",
  doctorName: "",
  allocationNotes: "",
};
interface IProps {
  handleClose: () => void;
  open: boolean;
  rn: number;
  userInfo: IUserInfo;
  roundInfo: Round;
  selectedFacem: Facem | null;
}

function PaperComponent(props: PaperProps) {
  const nodeRef = useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}

export default function DispositionDialog({
  handleClose,
  open,
  rn,
  userInfo,
  selectedFacem,
  roundInfo,
}: IProps) {
  const classes = useStyles();

  const { tempPatients, filteredPatients, tempRound } = useSelector(
    (state: RootState) => state.patient
  );

  const dispatch = useAppDispatch();
  const [allocateDr, setAllocateDr] = useState(defaultAllocateDr);
  const [currentPlan, setCurrentPlan] = useState("");
  const [currentMedDS, setCurrentMedDS] = useState("");
  const [savedInvestigation, setSavedInvestigation] = useState("");
  const [clinicalSaves, setClinicalSaves] = useState("");
  const [dialogLoading, setDialogLoading] = useState<boolean>(false);
  const [selectedPatient, setSelectedPatient] = useState<IData | undefined>();
  const drAllocated = allocateDr?.allocateDoctor === "Y";
  const [fieldErrors, setFieldErrors] = useState<{
    currentPlan: string;
    currentMedDS: string;
    doctorName: string;
    allocationNotes: string;
  }>({
    currentPlan: "",
    currentMedDS: "",
    doctorName: "",
    allocationNotes: "",
  });
  useEffect(() => {
    const patient = tempPatients.filter(
      (temPatient: IData) => temPatient.rn === rn
    );
    setCurrentPlan(patient[0].currentRoundPlan);
    setCurrentMedDS(patient[0].currentMedDS);
    setSelectedPatient(patient[0]);
    setSavedInvestigation(
      getSavedInvestigationOptionValue(patient[0]?.savedInvestigations || "")
    );
    setClinicalSaves(patient[0]?.clinicalSaves || "");
    setAllocateDr({
      allocateDoctor: patient[0]?.allocateDoctor || "N",
      doctorName: patient[0]?.doctorName || "",
      allocatedDatetime: patient[0]?.allocatedDatetime || "",
      allocationNotes: patient[0]?.allocationNotes || "",
    });
  }, [rn, tempPatients]);

  const handlePlanCancel = () => {
    handleClose();
  };

  const handlePlanSave = async () => {
    let errors: {
      currentPlan: string;
      currentMedDS: string;
      doctorName: string;
      allocationNotes: string;
    } = {
      currentPlan: "",
      currentMedDS: "",
      doctorName: "",
      allocationNotes: "",
    };

    if (drAllocated) {
      if (allocateDr?.doctorName.trim() === "") {
        errors = { ...errors, doctorName: "Doctor Name is required" };
      }
      if (allocateDr?.allocationNotes.trim() === "") {
        errors = { ...errors, allocationNotes: "Allocation Notes is required" };
      }
    } else {
      if (currentPlan.trim() === "") {
        errors = { ...errors, currentPlan: "Current Plan is required" };
      }

      if (currentMedDS.trim() === "") {
        errors = { ...errors, currentMedDS: "Disposition is required" };
      }
    }

    const nowSydney = moment()
      .utc()
      .tz("Australia/Sydney")
      .format("YYYY-MM-DD hh:mm:ss A");

    if (
      !errors.currentPlan &&
      !errors.currentMedDS &&
      !errors.doctorName &&
      !errors.allocationNotes
    ) {
      updateDiscussedPatient({
        roundInfo,
        setDialogLoading,
        actualFacem: selectedFacem?.facemNameInTwilio || userInfo?.name || null,
        patientInfo: {
          ...selectedPatient,
          currentRoundPlan: currentPlan,
          currentMedDS: currentMedDS,
          savedInvestigations: savedInvestigation,
          clinicalSaves: clinicalSaves,
          ...allocateDr,
          ...(drAllocated ? { allocatedDatetime: nowSydney } : {}),
        },
      });

      // update patient and filter patient
      const newPatients = tempPatients.map((patient) => {
        if (patient.rn === rn) {
          return {
            ...patient,
            currentRoundPlan: currentPlan,
            currentMedDS: currentMedDS,
            savedInvestigations: savedInvestigation,
            clinicalSaves: clinicalSaves,
            ...allocateDr,
            ...(drAllocated ? { allocatedDatetime: nowSydney } : {}),
          };
        } else {
          return patient;
        }
      });
      dispatch(setReduxTempPatients(newPatients));

      window.localStorage.setItem(
        `${tempRound.client} ${tempRound.batch}`,
        JSON.stringify(newPatients)
      );

      const newFilteredPatients = filteredPatients.map((patient) => {
        if (patient.rn === rn) {
          return {
            ...patient,
            currentRoundPlan: currentPlan,
            currentMedDS: currentMedDS,
            savedInvestigations: savedInvestigation,
            clinicalSaves: clinicalSaves,
            ...allocateDr,
            ...(drAllocated ? { allocatedDatetime: nowSydney } : {}),
          };
        } else {
          return patient;
        }
      });
      dispatch(setReduxFilteredPatients(newFilteredPatients));
      handleClose();
      setFieldErrors({
        currentPlan: "",
        currentMedDS: "",
        doctorName: "",
        allocationNotes: "",
      });
    } else {
      // There are validation errors, update the error state
      setFieldErrors(errors);
    }
  };

  const getNotes = () => {
    const patient = tempPatients.find(
      (temPatient: IData) => temPatient.rn === rn
    );
    const { lastRoundNotes } = patient || { lastRoundNotes: null };
    if (!lastRoundNotes || !lastRoundNotes.length) {
      return "";
    }

    return lastRoundNotes
      .map(
        ({
          batch,
          previousRoundNotes,
          previousMedDs,
          prevAllocateDoctor,
          prevAllocatedDatetime,
          prevAllocationNotes,
          prevDoctorName,
          prevSavedInvestigations,
          prevClinicalSaves,
        }) => {
          const sections: string[] = [`batch: ${batch}`];
          const hasRoundNotes = Boolean(previousRoundNotes);
          const hasDisposition = Boolean(previousMedDs);

          if (hasRoundNotes || hasDisposition) {
            const notesLine = hasRoundNotes
              ? `${previousRoundNotes}${
                  hasDisposition ? ` - DS - ${previousMedDs}` : ""
                }`
              : `DS: ${previousMedDs}`;
            sections.push(notesLine.trim());
          }

          if (prevSavedInvestigations) {
            sections.push(
              `Saved Investigations: ${formatSavedInvestigation(
                prevSavedInvestigations
              )}`
            );
          }

          if (prevClinicalSaves) {
            sections.push(`Clinical Saves: ${prevClinicalSaves}`);
          }

          if (prevAllocateDoctor === "Y") {
            sections.push(
              `Doctor: ${prevDoctorName || ""} | Allocated: ${(
                prevAllocatedDatetime || ""
              ).replace("T", " ")}\nAllocation Plan: ${
                prevAllocationNotes || ""
              }`
            );
          }

          return sections.join("\n");
        }
      )
      .join("\n\n");
  };
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type: string
  ) => {
    if (type === "currentPlan") {
      setCurrentPlan(e.target.value);

      // Check if the value is empty and set the error message accordingly
    } else if (type === "currentMedDS") {
      setCurrentMedDS(e.target.value);
    }
  };

  const handleSavedInvestigationChange = (
    event: ChangeEvent<{ value: unknown }>
  ) => {
    const rawValue = (event.target.value as string) || "";
    setSavedInvestigation(getSavedInvestigationOptionValue(rawValue));
  };

  const handleClinicalSavesChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setClinicalSaves(event.target.value);
  };

  return (
    <>
      {tempPatients.length && (
        <Dialog
          open={true}
          onClose={handleClose}
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title"
          maxWidth={"md"}
        >
          {dialogLoading && (
            <div className={classes.loadingIcon}>
              <CircularProgress />
            </div>
          )}
          <div
            className={`${classes.root} ${classes.size}`}
            style={{ cursor: "move" }}
            id="draggable-dialog-title"
          >
            <div className={classes.box}>
              <DialogTitle
                className={classes.summary}
                style={{ cursor: "move", margin: "0" }}
                id="draggable-dialog-title"
              >
                {`${selectedPatient?.firstname} ${selectedPatient?.lastname} ${
                  selectedPatient?.age
                }y.o ${selectedPatient?.gender === "F" ? "Female" : "Male"}`}
              </DialogTitle>
              <section className={classes.row}>
                <InputLabel shrink id="label" className={classes.label}>
                  Triage Information-From Site
                </InputLabel>
                <TextField
                  multiline
                  rows={3}
                  variant="outlined"
                  value={selectedPatient?.triageInformation}
                  disabled
                  style={{ width: "100%", background: "#d9dbdb" }}
                />
              </section>
              <section className={`${classes.row} ${classes.flex}`}>
                Vital Signs:
                <div className={classes.signs}>
                  <span>Temp: {selectedPatient?.temp}</span>
                  <span>HR: {selectedPatient?.hr}</span>
                  <span>BP: {selectedPatient?.bp}</span>
                  <span>RR: {selectedPatient?.rr}</span>
                  <span>SATS: {selectedPatient?.sats}</span>
                  <span>BGL: {selectedPatient?.bgl}</span>
                </div>
              </section>
              <section className={classes.row}>
                <InputLabel shrink id="label" className={classes.label}>
                  Previous Round Notes
                </InputLabel>
                <TextField
                  multiline
                  rows={6}
                  variant="outlined"
                  value={getNotes()}
                  disabled
                  style={{ width: "100%", background: "#d9dbdb" }}
                />
              </section>
              <section className={classes.row}>
                <InputLabel shrink id="label" className={classes.label}>
                  Current Round Plan
                </InputLabel>
                <TextField
                  multiline
                  rows={3}
                  variant="outlined"
                  style={{ width: "100%", background: "#fff" }}
                  value={currentPlan}
                  onChange={(e) => handleChange(e, "currentPlan")}
                  // error={Boolean(fieldErrors.currentPlan)}
                  // helperText={fieldErrors.currentPlan}
                />
                {!drAllocated && Boolean(fieldErrors.currentPlan) && (
                  <FormHelperText error className={classes.errText}>
                    {fieldErrors.currentPlan}
                  </FormHelperText>
                )}
              </section>
              <section className={classes.row}>
                <div className={classes.multiInputRow}>
                  <div className={classes.flex}>
                    <InputLabel shrink className={classes.label}>
                      Allocate Doctor
                    </InputLabel>
                    <Checkbox
                      color="primary"
                      checked={drAllocated}
                      onChange={(e) =>
                        setAllocateDr({
                          ...allocateDr,
                          allocateDoctor: e.target.checked ? "Y" : "N",
                        })
                      }
                    />
                  </div>
                  <div className={classes.flex}>
                    <InputLabel shrink className={classes.label}>
                      Doctor Name
                    </InputLabel>
                    <div style={{ position: "relative" }}>
                      <TextField
                        style={
                          drAllocated
                            ? { background: "#fff" }
                            : { background: "lightgrey" }
                        }
                        variant="outlined"
                        value={allocateDr?.doctorName}
                        size="small"
                        onChange={(e) =>
                          setAllocateDr({
                            ...allocateDr,
                            doctorName: e.target.value,
                          })
                        }
                        // error={
                        //   drAllocated ? Boolean(fieldErrors.doctorName) : false
                        // }
                        // helperText={drAllocated ? fieldErrors.doctorName : ""}
                        disabled={!drAllocated}
                      />
                      {drAllocated && Boolean(fieldErrors.doctorName) && (
                        <FormHelperText
                          error
                          className={classes.errText}
                          style={{
                            position: "absolute",
                            top: "40px",
                            left: "0",
                          }}
                        >
                          {fieldErrors.doctorName}
                        </FormHelperText>
                      )}
                    </div>
                  </div>
                  <div className={classes.flex}>
                    <InputLabel shrink className={classes.label}>
                      Date Time
                    </InputLabel>
                    <span className={classes.dateTime}>
                      {allocateDr?.allocatedDatetime}
                    </span>
                  </div>
                </div>
              </section>
              <section className={classes.row}>
                <InputLabel shrink id="label" className={classes.label}>
                  Allocation Plan
                </InputLabel>
                <TextField
                  multiline
                  rows={3}
                  variant="outlined"
                  style={
                    drAllocated
                      ? { background: "#fff", width: "100%" }
                      : { background: "lightgrey", width: "100%" }
                  }
                  value={allocateDr?.allocationNotes}
                  onChange={(e) =>
                    setAllocateDr({
                      ...allocateDr,
                      allocationNotes: e.target.value,
                    })
                  }
                  disabled={!drAllocated}
                />
                {drAllocated && Boolean(fieldErrors.allocationNotes) && (
                  <FormHelperText error className={classes.errText}>
                    {fieldErrors.allocationNotes}
                  </FormHelperText>
                )}
              </section>
              <section className={classes.row}>
                <div>Disposition</div>
                <RadioGroup
                  aria-labelledby="disposition-radio-buttons-group-label"
                  name="radio-buttons-group"
                  className={classes.radioGroup}
                  value={currentMedDS}
                  onChange={(e) => handleChange(e, "currentMedDS")}
                >
                  <FormControlLabel
                    value="Discharge"
                    control={<Radio size="small" />}
                    label={
                      <span className={classes.radioLabel}>Discharge</span>
                    }
                  />
                  <FormControlLabel
                    value="Continue Work Up"
                    control={<Radio size="small" />}
                    label={
                      <span className={classes.radioLabel}>
                        Continue Work Up
                      </span>
                    }
                  />
                  <FormControlLabel
                    value="Decision to Admit"
                    control={<Radio size="small" />}
                    label={
                      <span className={classes.radioLabel}>
                        Decision to Admit
                      </span>
                    }
                  />
                  <FormControlLabel
                    value="Transfer to External Hospital"
                    control={<Radio size="small" />}
                    label={
                      <span className={classes.radioLabel}>
                        Transfer to External Hospital
                      </span>
                    }
                  />
                </RadioGroup>
                {!drAllocated && Boolean(fieldErrors.currentMedDS) && (
                  <FormHelperText error className={classes.errText}>
                    {fieldErrors.currentMedDS}
                  </FormHelperText>
                )}
              </section>
              <section className={classes.row}>
                <InputLabel
                  shrink
                  id="saved-investigations"
                  className={classes.label}
                >
                  Saved Investigations
                </InputLabel>
                <TextField
                  select
                  variant="outlined"
                  value={savedInvestigation}
                  onChange={handleSavedInvestigationChange}
                  fullWidth
                  className={classes.selectField}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) {
                        return (
                          <span className={classes.selectPlaceholder}>
                            Select an option
                          </span>
                        );
                      }
                      return getSavedInvestigationOptionLabel(
                        selected as string
                      );
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {savedInvestigationGroups.reduce<React.ReactNode[]>(
                    (items, group) => {
                      items.push(
                        <ListSubheader
                          key={`${group.label}-header`}
                          disableSticky
                          style={{ pointerEvents: "none" }}
                        >
                          {group.label}
                        </ListSubheader>
                      );
                      group.options.forEach((option) => {
                        items.push(
                          <MenuItem
                            key={`${group.label} - ${option.value}`}
                            value={option.value}
                          >
                            {option.label}
                          </MenuItem>
                        );
                      });
                      return items;
                    },
                    []
                  )}
                </TextField>
              </section>
              <section className={classes.row}>
                <InputLabel
                  shrink
                  id="clinical-saves"
                  className={classes.label}
                >
                  Clinical Saves
                </InputLabel>
                <TextField
                  multiline
                  minRows={4}
                  variant="outlined"
                  value={clinicalSaves}
                  onChange={handleClinicalSavesChange}
                  fullWidth
                  className={classes.textAreaField}
                />
              </section>
              <div className={classes.btnGroup}>
                <Button
                  className={`${classes.btn} ${classes.cancelBtn}`}
                  variant="contained"
                  color="primary"
                  aria-controls="customized-menu"
                  aria-haspopup="true"
                  onClick={handlePlanCancel}
                >
                  cancel
                </Button>
                <Button
                  className={classes.btn}
                  variant="contained"
                  color="primary"
                  aria-controls="customized-menu"
                  aria-haspopup="true"
                  onClick={handlePlanSave}
                >
                  save
                </Button>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}
